/**
 * Utility functions for handling gRPC errors from URSULA and other services
 */

/**
 * gRPC Status Codes from https://grpc.github.io/grpc/core/md_doc_statuscodes.html
 */
export enum GrpcStatusCode {
  OK = 0,
  CANCELLED = 1,
  UNKNOWN = 2,
  INVALID_ARGUMENT = 3,
  DEADLINE_EXCEEDED = 4,
  NOT_FOUND = 5,
  ALREADY_EXISTS = 6,
  PERMISSION_DENIED = 7,
  RESOURCE_EXHAUSTED = 8,
  FAILED_PRECONDITION = 9,
  ABORTED = 10,
  OUT_OF_RANGE = 11,
  UNIMPLEMENTED = 12,
  INTERNAL = 13,
  UNAVAILABLE = 14,
  DATA_LOSS = 15,
  UNAUTHENTICATED = 16,
}

/**
 * Interface for gRPC-like errors
 */
export interface GrpcError extends Error {
  code?: number;
  details?: string;
  cause?: {
    code?: number;
    message?: string;
  };
}

/**
 * Checks if an error is a gRPC RESOURCE_EXHAUSTED error indicating quota exhaustion or subscription issues
 * @param error - The error object to check
 * @returns true if the error indicates quota exhaustion or subscription expiration from URSULA
 */
export function isQuotaExhaustedError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  const grpcError = error as GrpcError;

  // Check for gRPC status codes
  if (
    grpcError.code === GrpcStatusCode.RESOURCE_EXHAUSTED ||
    grpcError.cause?.code === GrpcStatusCode.RESOURCE_EXHAUSTED
  ) {
    return true;
  }

  const patterns = [
    "resource_exhausted",
    "quota",
    "not enough quota available",
    "subscription",
    "expired",
    "limit exceeded",
    "usage limit",
  ];

  // Combine message and details into one array to check
  const fields = [grpcError.message, grpcError.details];

  for (const field of fields) {
    if (typeof field !== "string") continue;
    const text = field.toLowerCase();
    if (patterns.some((p) => text.includes(p))) {
      return true;
    }
  }

  return false;
}

/**
 * Extracts quota information from a RESOURCE_EXHAUSTED error message
 * @param error - The error object
 * @returns Quota information if available, null otherwise
 */
export function extractQuotaInfo(error: unknown): {
  totalQuota?: number;
  remainingQuota?: number;
  requestedEffort?: number;
  effortType?: number;
} | null {
  if (!error || typeof error !== "object") return null;

  const grpcError = error as GrpcError;
  const message = grpcError.message || grpcError.details || "";

  // Pattern from URSULA error: "Not enough quota available. Total quota for current period and effort type 0 is 10000.0, remaining quota is 9885.0, and request had an effort of 1000000.0"
  const quotaPattern =
    /Total quota for current period and effort type (\d+) is ([\d.]+), remaining quota is ([\d.]+), and request had an effort of ([\d.]+)/i;
  const match = message.match(quotaPattern);

  if (match) {
    return {
      effortType: parseInt(match[1]),
      totalQuota: parseFloat(match[2]),
      remainingQuota: parseFloat(match[3]),
      requestedEffort: parseFloat(match[4]),
    };
  }

  return null;
}

/**
 * Determines if the error is due to usage period expiration (totalQuota = 0.0) or character limit
 * @param quotaInfo - Quota information extracted from error
 * @returns true if usage period expired, false if character limit exceeded
 */
export function isUsagePeriodExpired(
  quotaInfo: ReturnType<typeof extractQuotaInfo>
): boolean {
  return quotaInfo?.totalQuota === 0.0;
}

/**
 * Extracts quota values for use in translation interpolation
 * @param quotaInfo - Quota information extracted from error
 * @returns Object with formatted quota values for translation or null
 */
export function getQuotaTranslationValues(
  quotaInfo: ReturnType<typeof extractQuotaInfo>
): { remaining: string; total: string; requested: string } | null {
  if (!quotaInfo) {
    return null;
  }

  const { totalQuota, remainingQuota, requestedEffort } = quotaInfo;

  // If totalQuota is 0.0, it means usage period is expired, no detailed info needed
  if (totalQuota === 0.0) {
    return null;
  }

  // For character limit issues, return formatted values for translation
  if (totalQuota && remainingQuota !== undefined && requestedEffort) {
    return {
      remaining: remainingQuota.toLocaleString(),
      total: totalQuota.toLocaleString(),
      requested: requestedEffort.toLocaleString(),
    };
  }

  return null;
}
